/**
 * IntroSequence.tsx — the boot sequence for Sorry Boss (BETA 22.51c).
 *
 * Dylan's rip design, v2 tuning (2026-07-01):
 *  - The Blame Culture logo is BAKED INTO the black screen: both tear halves
 *    carry identical copies of the full frame, so when the screen rips the
 *    logo genuinely tears in half with it.
 *  - The rip is VERTICAL (left/right halves) and the tear line CURVES — a
 *    jittered S-curve down the middle, not a straight zigzag.
 *  - The logo sits a beat longer before the tear (Dylan: "let it sit there
 *    for a second more before the rip").
 *
 * After the tear the live StartMenu is revealed beneath (bg zoomed + SORRY
 * BOSS logo centered) and StartMenu runs the rest of the entrance (zoom out,
 * logo to the bottom-RIGHT, fast multi-loop roster sweep onto DUCK, big
 * smoke, UI slamming down from the top). Esc skips. Preview: ?intro=1.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { resolveAsset } from '../game/uiAssets';
import { initAudio, resumeAudio, startBgm, playMenuSound, preloadMenuSounds, startMenuAmbience } from '../game/audio';

// Dylan's logo art ("BlameCulture.PNG" — solid black baked in, NO glow ever).
const STUDIO_LOGO = resolveAsset('/intro/blame_culture_labs_logo.jpg');

const STUDIO_MS = 3650;  // logo reveals by ~0.9s, then SITS until the tear
const RIP_MS = 760;

// The curved tear line: an S-curve down the screen with small jitter, as
// (x%, y%) pairs top→bottom. LEFT half owns it as its right edge; RIGHT half
// reuses the SAME points as its left edge → the halves tile seamlessly.
const TEAR: Array<[number, number]> = [
  [52.0, 0], [49.8, 7], [47.9, 14], [46.9, 21], [47.3, 28], [48.6, 35],
  [50.6, 42], [52.8, 49], [54.6, 56], [55.7, 63], [55.9, 70], [55.0, 77],
  [53.2, 84], [51.0, 91], [49.6, 96], [49.0, 100],
];
const down = TEAR.map(([x, y]) => `${x}% ${y}%`).join(', ');
// The right half's edge is shifted 0.4% LEFT of the left half's edge → the
// halves OVERLAP along the seam. Both render identical pixels there (same
// black + same logo at the same position), so the overlap is invisible —
// but the sub-pixel hairline crack between two abutting clip-paths (the
// color leak Dylan saw on his Retina display) becomes impossible.
const up = TEAR.slice().reverse().map(([x, y]) => `${x - 0.4}% ${y}%`).join(', ');
const LEFT_CLIP = `polygon(0% 0%, ${down}, 0% 100%)`;
const RIGHT_CLIP = `polygon(${up}, 100% 0%, 100% 100%, ${TEAR[TEAR.length - 1][0] - 0.4}% 100%)`;

type Phase = 'studio' | 'rip';

export default function IntroSequence({ onComplete }: { onComplete: () => void }) {
  const [phase, setPhase] = useState<Phase>('studio');
  const doneRef = useRef(false);

  // Music from frame zero where allowed (Mac app); one-time gesture unlock
  // for browsers that block autoplay.
  useEffect(() => {
    // BETA 22.52b — initAudio() FIRST. React runs child effects before parent
    // effects, so this intro mounts BEFORE Home's initAudio() — startBgm() was
    // a silent no-op (no AudioContext yet) and the APP booted with no music
    // until the first click (which the app design removed!). initAudio is
    // idempotent, so claiming it here is safe and makes music truly start on
    // the Blame Culture card in the app (browsers still need the gesture).
    // BETA 22.53 — the SONG does NOT start here anymore. Dylan: the track's
    // first "SORRY BOSS" scream (measured at ~1.45s into the song from his
    // vocal stem) must LAND exactly when the tear reveals the SORRY BOSS
    // logo. Studio card = world ambience only; the song is scheduled below
    // so scream time == reveal time.
    try { initAudio(); resumeAudio(); startMenuAmbience(); preloadMenuSounds(); } catch { /* */ }
    const unlock = () => {
      try { initAudio(); resumeAudio(); } catch { /* */ }
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);

  const songStartedRef = useRef(false);
  const startSong = useCallback(() => {
    if (songStartedRef.current) return;
    songStartedRef.current = true;
    try { void startBgm(); } catch { /* */ }
  }, []);

  const finish = useCallback(() => {
    if (doneRef.current) return; doneRef.current = true;
    startSong();          // Esc-skip must never leave the menu songless
    onComplete();
  }, [onComplete, startSong]);

  useEffect(() => {
    // The scream (song t≈1.45s) hits as the gap opens wide enough to read the
    // logo (~250ms into the rip): song starts at STUDIO_MS + 250 − 1450.
    const toSong = window.setTimeout(startSong, Math.max(0, STUDIO_MS + 250 - 1450));
    const toRip = window.setTimeout(() => { setPhase('rip'); playMenuSound('ui_rip_tear', 0.85); }, STUDIO_MS);
    const toDone = window.setTimeout(finish, STUDIO_MS + RIP_MS);
    return () => { window.clearTimeout(toSong); window.clearTimeout(toRip); window.clearTimeout(toDone); };
  }, [finish, startSong]);

  // Esc skips the whole intro (StartMenu's entrance also skips on any input).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') finish(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [finish]);

  // Both halves are ALWAYS mounted with identical full-frame content (black +
  // logo, same reveal animation) — at rest they tile into one solid screen, so
  // the tear rips the logo itself in half with zero swap flicker.
  const half = (side: 'L' | 'R') => (
    <div key={side} style={{
      // 4% vertical overshoot: when the halves rotate during the fling, their
      // tilted top/bottom edges stay beyond the screen instead of exposing
      // slivers of what's behind (16:10 displays made this visible).
      position: 'absolute', inset: '-4% 0',
      clipPath: side === 'L' ? LEFT_CLIP : RIGHT_CLIP,
      animation: phase === 'rip' ? `rip_${side} ${RIP_MS}ms cubic-bezier(0.55,0,0.8,0.45) both` : undefined,
      willChange: 'transform',
    }}>
      <div style={{ position: 'absolute', inset: 0, background: '#000' }} />
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <img src={STUDIO_LOGO} alt={side === 'L' ? 'Blame Culture Labs' : ''}
          style={{ width: 'min(86vw, 1180px)', maxHeight: '72vh', objectFit: 'contain',
            animation: 'bc_reveal 2.4s cubic-bezier(0.16,0.84,0.28,1) both' }} />
      </div>
    </div>
  );

  return (
    // BETA 22.53 — root is SOLID BLACK through the studio card (Dylan's 16:10
    // MacBook showed color leaking at the tear seam / screen edges — "the
    // black screen isn't tall enough"): no clip hairline or coverage bug can
    // leak while the Blame Culture logo sits. It goes transparent only for
    // the rip itself, so the gap genuinely reveals the menu's logo.
    <div style={{ position: 'fixed', inset: 0, zIndex: 10050, overflow: 'hidden', pointerEvents: 'none',
      background: phase === 'studio' ? '#000' : 'transparent' }}>
      {half('L')}
      {half('R')}
      <style>{`
        @keyframes rip_L {
          0%   { transform: translateX(0) rotate(0deg); }
          14%  { transform: translateX(-1.2%) rotate(-0.35deg); }
          100% { transform: translateX(-120%) rotate(-2.6deg); }
        }
        @keyframes rip_R {
          0%   { transform: translateX(0) rotate(0deg); }
          14%  { transform: translateX(1.2%) rotate(0.35deg); }
          100% { transform: translateX(120%) rotate(2.6deg); }
        }
        /* Reveal flicker — ends VISIBLE and sits (the tear takes it apart). */
        @keyframes bc_reveal {
          0%   { opacity: 0; transform: scale(0.82); }
          7%   { opacity: 0.12; }
          10%  { opacity: 0.9; }
          14%  { opacity: 0.28; }
          22%  { opacity: 1; transform: scale(1.04); }
          34%  { transform: scale(1); }
          100% { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}
